import React, { useRef, useState, useEffect } from "react";
import { useIntl } from "react-intl";

import DisplayFields from "../DisplayFields";
import SelectMeetingType from "../SelectMeetingType";
import MeetingClientSearch from "../MeetingClientSearch";
import LocationSearch from "../LocationSearch";
import DatePicker from "../DatePicker";
import MeetingDescription from "../MeetingDescription";
import UserSearch from "../../../Search/UserSearch";

import { InputLabel, FormControl } from "@mui/material";
import "../styles/inputTitle.css";
import Throbber from "../../../../style/Throbber";
import {
    HiOutlineClock,
    HiOutlineBuildingStorefront,
    HiBars3BottomLeft,
    HiOutlineUserGroup,
    HiXMark,
    HiOutlineMapPin,
} from "react-icons/hi2";

const CreateEditMeetingModal = ({
    initialEventData,
    eventData,
    user_id,
    setEventData,
    meetingTypes,
    detailsTrue,
    isSubmitting,
    error,
    success,
    iconSize,
    isLoading,
    typeError,
    setTypeError,
    dateError,
    setDateError,
    clientError,
    setClientError,
    clients,
    selectedClient,
    setSelectedClient,
    setSuggestions,
    handleSave,
    attendees,
    selectedMeeting,
    setSelectedMeeting,
    locale,
}) => {
    const [hasChanges, setHasChanges] = useState(false);
    const [availableAttendees, setAvailableAttendees] = useState(
        attendees.filter((a) => a.user_id != user_id)
    );
    const addAttendee = (attendeeId) => {
        setEventData((prevData) => {
            if (!prevData.attendees.includes(attendeeId)) {
                return {
                    ...prevData,
                    attendees: [...prevData.attendees, attendeeId],
                };
            }
            return prevData;
        });
        setHasChanges(true);
        setAvailableAttendees((prevData) =>
            prevData.filter((user) => user.user_id !== attendeeId)
        );
    };

    const intl = useIntl();
    const titleRef = useRef(null);
    useEffect(() => {
        if (titleRef.current && !detailsTrue) {
            setTimeout(() => {
                titleRef.current.focus();
            }, 0);
        }
    }, []);

    const handleChange = (e) => {
        if (e && e.target) {
            // This branch handles regular input fields
            const { name, value } = e.target;
            setEventData((prevData) => ({ ...prevData, [name]: value }));
            if (!hasChanges && initialEventData[name] !== value) {
                setHasChanges(true);
            }
        } else if (e && typeof e === "object") {
            // This branch handles date objects from DatePicker
            const { name, value } = e;
            setEventData((prevData) => ({ ...prevData, [name]: value }));
        }
    };

    const [selectedMeetingType, setSelectedMeetingType] = useState(
        initialEventData.meeting_type_id || ""
    );

    // Find the selected meeting type object

    useEffect(() => {
        const selectedMeeting = meetingTypes.find(
            (type) => type.meeting_type_id === selectedMeetingType
        );
        setSelectedMeeting(selectedMeeting);
    }, [meetingTypes, selectedMeetingType]);

    // Combined change handler
    const handleSelectChange = (e) => {
        const selectedValue = e.target.value;
        setSelectedMeetingType(selectedValue); // Update local state

        setEventData((prevData) => ({
            ...prevData,
            meeting_type_id: selectedValue,
        }));
        if (!hasChanges && initialEventData.meeting_type_id !== selectedValue) {
            setHasChanges(true);
        }

        if (typeError) {
            setTypeError(false);
        }
    };

    const removeAttendee = (attendeeId) => {
        setEventData((prevData) => ({
            ...prevData,
            attendees: prevData.attendees.filter((id) => id !== attendeeId),
        }));
        setHasChanges(true);
    };

    // Determine whether to show the extra input
    const [showExtraInput, setShowExtraInput] = useState(false);

    useEffect(() => {
        showClientInput(selectedMeeting);
    }, [selectedMeeting]);

    const showClientInput = (selectedMeeting) => {
        if (
            selectedMeeting?.is_client_meeting === true ||
            initialEventData.client
        ) {
            setShowExtraInput(true);
            if (eventData.client && eventData.client.client_name !== null) {
                setSelectedClient(eventData.client.client_name);
            } else setSelectedClient("");
        } else {
            setShowExtraInput(false);
            setEventData((prev) => ({ ...prev, client: null }));
            setSelectedClient("");
        }
    };

    useEffect(() => {
        let newAddress;
        if (eventData.client) {
            const filteredClient = clients.find(
                (client) => client.client_id === eventData.client.client_id
            );

            if (filteredClient) {
                newAddress = {
                    lat: filteredClient.lat || "",
                    long: filteredClient.long || "",
                    address_1: filteredClient.address_1 || "",
                    zip_code: filteredClient.zip_code || "",
                    city: filteredClient.city || "",
                    country: filteredClient.country || "",
                };
            }
        } else {
            newAddress = {
                client: null,
            };
        }
        // Update your state or form with the new address
        setEventData((prevData) => ({
            ...prevData,
            ...newAddress,
        }));
    }, [eventData.client]);

    return (
        <div className="mr-1 md:mr-0 pl-1 pr-1 md:pl-10 md:pr-10 pb-5 h-full  overflow-y-auto no-scrollbar">
            <div className="flex flex-row w-full items-center md:mb-4">
                <div className="w-full">
                    <h2 className="text-lg md:text-xl flex justify-left w-9/10 ml-10">
                        <input
                            ref={titleRef}
                            type="text"
                            name="title"
                            value={
                                eventData.title === "null"
                                    ? intl.formatMessage({
                                          id: "createMeetingModal.editTitle",
                                      })
                                    : eventData.title
                            }
                            onChange={handleChange}
                            placeholder={intl.formatMessage({
                                id: "createMeetingModal.editTitle",
                            })}
                            className="custom-input"
                        />
                    </h2>
                </div>
                {isSubmitting && (
                    <div className="absolute top-1/2 left-1/2 z-10">
                        <Throbber />
                    </div>
                )}
            </div>
            {error && (
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded">
                    {error}
                </div>
            )}

            {success && (
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-2 rounded">
                    {success}
                </div>
            )}

            <form className={`flex flex-col md:pt-2`}>
                {/*
                        SELECT MEETING TYPE
                        */}

                <DisplayFields
                    icon={
                        <div
                            className={`mt-4 h-4 w-4 rounded-full`}
                            style={{
                                height: "14px",
                                width: "14px",
                                backgroundColor: meetingTypes?.find(
                                    (type) =>
                                        type.meeting_type_id ===
                                        eventData.meeting_type_id
                                )?.color,
                            }}
                        />
                    }
                    center={true}
                    isForm={false}
                >
                    <FormControl
                        fullWidth
                        variant="standard"
                        sx={{ minWidth: 120 }}
                    >
                        {!detailsTrue && (
                            <InputLabel
                                id="meeting-type"
                                error={typeError}
                                sx={{
                                    backgroundColor: "white",
                                    paddingLeft: "5px",
                                    paddingRight: "5px",
                                }}
                            >
                                {intl.formatMessage({
                                    id: "meetingModal.selectError",
                                })}
                            </InputLabel>
                        )}

                        <SelectMeetingType
                            selectedMeetingType={selectedMeetingType}
                            handleSelectChange={handleSelectChange}
                            typeError={typeError}
                            meetingTypes={meetingTypes}
                        />
                    </FormControl>
                </DisplayFields>

                {/*
                        SELECT CLIENT
                        */}

                {showExtraInput && (
                    <DisplayFields
                        icon={<HiOutlineBuildingStorefront size={iconSize} />}
                        center={true}
                        isForm={false}
                    >
                        <MeetingClientSearch
                            selectedMeetingTypeData={selectedMeeting}
                            isLoading={isLoading}
                            clients={clients}
                            selectedClient={selectedClient}
                            setSelectedClient={setSelectedClient}
                            setSuggestions={setSuggestions}
                            setEventData={setEventData}
                            clientError={clientError}
                            setClientError={setClientError}
                            eventData={eventData}
                            meetingTypes={meetingTypes}
                            setHasChanges={setHasChanges}
                        />
                    </DisplayFields>
                )}

                <DisplayFields
                    icon={<HiOutlineMapPin size={iconSize} />}
                    center={eventData.client ? true : false}
                    isForm={eventData.client ? false : true}
                >
                    {" "}
                    {eventData.client ? (
                        <div className="ml-3 py-1">
                            {eventData.address_1} {eventData.zip_code}{" "}
                            {eventData.city} {eventData.country}
                        </div>
                    ) : (
                        <LocationSearch
                            eventData={eventData}
                            setEventData={setEventData}
                            setHasChanges={setHasChanges}
                        />
                    )}
                </DisplayFields>

                {/*
                            SELECT DATE
                        */}
                <DisplayFields
                    icon={<HiOutlineClock size={iconSize} />}
                    center={true}
                    isForm={false}
                >
                    <DatePicker
                        locale={locale}
                        eventData={eventData}
                        handleChange={handleChange}
                        dateError={dateError}
                        setDateError={setDateError}
                    />
                </DisplayFields>

                <DisplayFields
                    icon={<HiBars3BottomLeft size={iconSize} />}
                    center={false}
                    isForm={true}
                >
                    <MeetingDescription
                        eventData={eventData}
                        handleChange={handleChange}
                    />
                </DisplayFields>

                {/*
                        display user search bar is user is the organizer of the meeting
                        */}

                <DisplayFields
                    icon={<HiOutlineUserGroup size={iconSize} />}
                    center={true}
                    isForm={false}
                >
                    {" "}
                    <UserSearch
                        placeholder="meetingModal.addAttendee"
                        usersList={availableAttendees}
                        sharedCalUsersList={null}
                        handleSelect={addAttendee}
                    />
                </DisplayFields>
                <ul className="flex-1 pt-3 md:pt-1 flex flex-col gap-1 ml-10">
                    {eventData.attendees
                        .filter((attendeeId) => attendeeId !== user_id) // Exclude current user_id
                        .map((attendeeId) => {
                            const attendee = attendees.find(
                                (a) => a.user_id === attendeeId
                            );
                            if (!attendee) return null;
                            return (
                                <li
                                    key={attendeeId}
                                    className="flex justify-between hover:bg-gray-50 px-2 py-1 rounded-md"
                                >
                                    <span>
                                        {attendee.first_name}{" "}
                                        {attendee.last_name}
                                    </span>
                                    <button
                                        onClick={() =>
                                            removeAttendee(attendeeId)
                                        }
                                        type="button"
                                        className="hover:bg-gray-200 rounded-full p-1"
                                    >
                                        <HiXMark />
                                    </button>
                                </li>
                            );
                        })}
                </ul>
            </form>

            <div className="flex md:justify-end mt-4 md:gap-3 gap-2 justify-center">
                {/*
                        logic change to display save button
                        */}

                <button
                    onClick={handleSave}
                    className="hidden md:block text-black bg-gold px-4 py-2 rounded-full disabled:bg-gray-200 hover:bg-black hover:text-white hover:disabled:text-black w-1/3 md:w-1/4"
                    disabled={!hasChanges}
                >
                    {intl.formatMessage({
                        id: detailsTrue
                            ? "editMeetingModal.saveButton"
                            : "createMeetingModal.saveButton",
                    })}
                </button>
            </div>
        </div>
    );
};

export default CreateEditMeetingModal;
